
import React, { useState } from 'react';
import { X, Check, Loader2, ArrowRight, ShieldCheck, Sparkles, Zap, Crown } from 'lucide-react';
import { STRIPE_PLANS } from '../constants';
import { PlanTier } from '../types';

interface PricingModalProps {
  onClose: () => void;
  onSelectPlan: (priceId: string) => void;
  isLoading: boolean;
  currentTier?: PlanTier;
  isTrial?: boolean; // Novo prop para indicar que o usuário está no período de teste
}

export const PricingModal: React.FC<PricingModalProps> = ({ onClose, onSelectPlan, isLoading, currentTier = 'trial', isTrial = false }) => {
  const [cycle, setCycle] = useState<'monthly' | 'yearly'>('monthly');

  // Lógica de exibição:
  // Mostra Premium se:
  // 1. O usuário NÃO for Premium e NÃO for Pro+ (ex: Trial expirado ou Free)
  // 2. OU se o usuário for Trial (mesmo que tenha tier 'premium' para acesso, ele ainda não pagou)
  const showPremiumOption = (currentTier !== 'premium' && currentTier !== 'pro_plus') || isTrial;

  return (
    <div className="fixed inset-0 z-[300] bg-slate-950/80 backdrop-blur-md flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn" onClick={onClose}>
      <div 
        className="bg-slate-950 w-full sm:max-w-5xl h-[95vh] sm:h-auto sm:max-h-[90vh] rounded-t-[2.5rem] sm:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col border border-slate-800" 
        onClick={e => e.stopPropagation()}
      >
        {/* Background Decor */}
        <div className="absolute top-0 inset-x-0 h-96 bg-gradient-to-b from-emerald-900/20 to-transparent pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none"></div>

        {/* Header */}
        <div className="p-6 sm:p-10 flex justify-between items-start shrink-0 z-10 relative">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-3">
               <Crown size={12} /> {showPremiumOption ? 'Desbloqueie todo o potencial' : 'Upgrade de Conta'}
            </div>
            <h2 className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tighter leading-none mb-2">
              {showPremiumOption ? 'Escolha seu Plano' : 'Evolua para o PRO+'}
            </h2>
            <p className="text-slate-400 text-sm font-medium">
              {showPremiumOption 
                ? 'Cancele ou altere sua assinatura a qualquer momento.' 
                : 'Acesse funcionalidades exclusivas para profissionais e múltiplas obras.'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 bg-slate-900 rounded-full hover:bg-slate-800 transition-colors border border-slate-800 text-slate-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-10 pt-0 z-10 relative">
          
          {/* Toggle Mensal/Anual */}
          <div className="flex justify-center mb-12">
            <div className="bg-slate-900 p-1.5 rounded-full flex relative border border-slate-800">
              {/* Pill Background Animation */}
              <div 
                className={`absolute top-1.5 bottom-1.5 w-[calc(50%-6px)] bg-slate-800 rounded-full transition-all duration-300 shadow-sm ${cycle === 'monthly' ? 'left-1.5' : 'left-[calc(50%+1.5px)]'}`}
              ></div>
              
              <button 
                onClick={() => setCycle('monthly')} 
                className={`relative z-10 px-6 sm:px-8 py-2.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest transition-colors ${cycle === 'monthly' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
              >
                Mensal
              </button>
              <button 
                onClick={() => setCycle('yearly')} 
                className={`relative z-10 px-6 sm:px-8 py-2.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-widest transition-colors ${cycle === 'yearly' ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}
              >
                Anual
                <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-emerald-400 text-emerald-950 border border-emerald-300 text-[10px] sm:text-xs px-2.5 py-0.5 rounded-full font-black shadow-[0_0_15px_rgba(52,211,153,0.6)] whitespace-nowrap">
                  20% OFF
                </span>
              </button>
            </div>
          </div>

          {/* Cards Grid - Centraliza se tiver apenas 1 card */}
          <div className={`grid gap-6 max-w-4xl mx-auto items-stretch ${showPremiumOption ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1 max-w-md'}`}>
            
            {/* PLANO PREMIUM (Branco com Verde) - SÓ MOSTRA SE NÃO FOR PREMIUM (OU SE FOR TRIAL) */}
            {showPremiumOption && (
              <div className="group relative rounded-[2rem] bg-white border border-slate-200 shadow-xl flex flex-col overflow-hidden">
                 <div className="p-8 pb-0">
                    <div className="flex justify-between items-start mb-4">
                       <div>
                          <h3 className="text-xl font-black text-emerald-950 uppercase tracking-tight">{STRIPE_PLANS.premium.label}</h3>
                          <p className="text-xs text-slate-500 font-bold mt-1">{STRIPE_PLANS.premium.description}</p>
                       </div>
                       <div className="p-3 bg-emerald-50 rounded-xl text-emerald-600 border border-emerald-100">
                          <ShieldCheck size={20} />
                       </div>
                    </div>
                    
                    <div className="flex items-baseline gap-1 mt-6">
                       <span className="text-5xl font-black text-emerald-950 tracking-tight">R$ {STRIPE_PLANS.premium.price[cycle].toFixed(2).replace('.', ',')}</span>
                       <span className="text-xs text-emerald-900/60 font-black uppercase tracking-wide">/{cycle === 'monthly' ? 'mês' : 'ano'}</span>
                    </div>
                 </div>
                 
                 <div className="p-8 flex-1 flex flex-col">
                    <div className="h-px bg-slate-100 w-full mb-6"></div>
                    <ul className="space-y-4 mb-8 flex-1">
                       {STRIPE_PLANS.premium.features.map((feat, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm text-emerald-900 font-bold">
                             <div className="mt-0.5 p-0.5 rounded-full bg-emerald-100 text-emerald-600">
                               <Check size={10} strokeWidth={4}/>
                             </div>
                             <span>{feat}</span>
                          </li>
                       ))}
                    </ul>
                    <button 
                       onClick={() => onSelectPlan(STRIPE_PLANS.premium[cycle])}
                       disabled={isLoading}
                       className="w-full py-4 rounded-xl border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-black uppercase tracking-widest text-xs transition-all flex items-center justify-center gap-2"
                    >
                       {isLoading ? <Loader2 size={16} className="animate-spin"/> : 'Selecionar Premium'}
                    </button>
                 </div>
              </div>
            )}

            {/* PLANO PRO+ (Efeito Respirar) */}
            <div className="group relative rounded-[2rem] border-2 border-emerald-500/50 bg-emerald-950/40 hover:bg-emerald-900/30 transition-all flex flex-col shadow-2xl shadow-emerald-500/20 md:-translate-y-4 animate-card-breathing">
               {/* Badge Popular */}
               <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-slate-950 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 z-20">
                  <Sparkles size={12} fill="currentColor" /> {showPremiumOption ? 'Recomendado' : 'Próximo Nível'}
               </div>

               <div className="p-8 pb-0 relative">
                  <div className="absolute top-0 right-0 p-8 opacity-20 text-emerald-500 pointer-events-none">
                     <Zap size={64} fill="currentColor" />
                  </div>

                  <div className="flex justify-between items-start mb-4 relative z-10">
                     <div>
                        <h3 className="text-xl font-black text-white uppercase tracking-tight flex items-center gap-2">
                           {STRIPE_PLANS.pro.label}
                        </h3>
                        <p className="text-xs text-emerald-200/70 font-medium mt-1">{STRIPE_PLANS.pro.description}</p>
                     </div>
                     <div className="p-3 bg-emerald-500 text-slate-950 rounded-xl shadow-lg shadow-emerald-500/20">
                        <Crown size={20} fill="currentColor" />
                     </div>
                  </div>
                  
                  <div className="flex items-baseline gap-1 mt-6 relative z-10">
                     <span className="text-5xl font-black text-white tracking-tight drop-shadow-lg">R$ {STRIPE_PLANS.pro.price[cycle].toFixed(2).replace('.', ',')}</span>
                     <span className="text-xs text-emerald-200/60 font-bold uppercase tracking-wide">/{cycle === 'monthly' ? 'mês' : 'ano'}</span>
                  </div>
               </div>
               
               <div className="p-8 flex-1 flex flex-col">
                  <div className="h-px bg-emerald-500/20 w-full mb-6"></div>
                  <ul className="space-y-4 mb-8 flex-1">
                     {STRIPE_PLANS.pro.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-white font-bold">
                           <div className="mt-0.5 p-0.5 rounded-full bg-emerald-500 text-slate-950 shadow-sm shadow-emerald-500/50">
                             <Check size={10} strokeWidth={4}/>
                           </div>
                           <span>{feat}</span>
                        </li>
                     ))}
                  </ul>
                  <button 
                     onClick={() => onSelectPlan(STRIPE_PLANS.pro[cycle])}
                     disabled={isLoading}
                     className="w-full py-4 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs uppercase tracking-widest shadow-xl shadow-emerald-600/20 hover:shadow-emerald-500/40 transition-all flex items-center justify-center gap-2 group/btn transform hover:scale-[1.02]"
                  >
                     {isLoading ? <Loader2 size={16} className="animate-spin"/> : <span className="flex items-center gap-2">Assinar PRO+ Agora <ArrowRight size={16} className="group-hover/btn:translate-x-1 transition-transform"/></span>}
                  </button>
               </div>
            </div>

          </div>
        </div>
        
        {/* Footer Trust */}
        <div className="p-4 bg-slate-950/50 border-t border-slate-800 text-center shrink-0 z-10 rounded-b-[2.5rem]">
            <div className="flex justify-center gap-8 opacity-50">
               <div className="flex items-center gap-2">
                  <ShieldCheck size={14} className="text-emerald-500"/>
                  <span className="text-[10px] text-slate-300 font-bold uppercase tracking-widest">Pagamento Seguro</span>
               </div>
               <div className="flex items-center gap-2">
                  <Check size={14} className="text-emerald-500"/>
                  <span className="text-[10px] text-slate-300 font-bold uppercase tracking-widest">Garantia de 7 dias</span>
               </div>
            </div>
        </div>
      </div>
    </div>
  );
};
